import {
  EXPENSES,
  REVENUE,
  OTHER_INCOME,
  DIRECT_COSTS,
  SALARIES_OF_ASSIGNED_EMPLOYEES,
  PROJECT_EXPENSES,
  GROSS_INCOME,
  INDIRECT_COSTS,
  ADMINISTRATIVE_PERSONNEL,
  EMPLOYEES_ON_BENCH,
  NET_INCOME,
  SALARIES,
  ALL_CURRENCIES,
} from '../constants/constants';

import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import { getValue, getValueByProject, getChildren, getChildrenByProject, getTempPeriod } from './treeCreationMethods';

/**
    * Description createTreeArr
    * Purpose of the function:
    * Create data tree
    * The function create an array of objects in which it accepts the component <TableTree/>
    * Object has name for left part table and value for right part table
    * Also Object can has children which content with the same objects
    * If object has children, this children is considered level 2, if object in children has own children is considered level 3
    * So component <TableTree/> render tree data
    *
    * @param {array} dateColumnsPeriod - Default 12 periods for columns 
    * @param {array} dataCashReceiptCustomers - Data from request
    * @param {array} dataCashReceiptCustomersChildren - Data from request
    * @param {array} dataSalaries - Data from request
    * @param {array} dataSalariesAdmin - Data from request
    * @param {array} dataSalariesAssigned - Data from request
    * @param {array} dataSalariesNotAssigned - Data from request
    * @param {array} dataExpenses - Data from request
    * @param {array} dataExpensesByCategory - Data from request
    * @param {array} dataExtraIncome - Data from request
    * @param {array} dataExtraIncomeByCategory - Data from request
    * @param {array} dataInvestments - Data from request
    * @param {array} dataInvestmentsByCategory - Data from request
    * @param {array} dataDistributions - Data from request
    * @param {array} dataDistributionsByCategory - Data from request
    * @param {array} dataTaxes - Data from request
    * @param {array} dataTaxesByCategory - Data from request
    * @param {array} dataBalanceChanges - Data from request
    * @param {array} dataDiscrepancy - Data from request
    * @param {array} dataExchangeOperations - Data from request
    * @param {array} dataNetCashFlow - Data from request
    * @param {string} currency - Selected currency, createTreeArr return array just data which has this currency
                      
    * @return {array} - returns an array of objects in which it accepts the component <TableTree/> 
   */

let fullPeriod = null;
let tempPeriod = [];
let period = null;

const createTreeArr = (
  dateColumnsPeriod,
  dataRevenue,
  dataRevenueByCategory,
  dataOtherIncome,
  dataOtherIncomeByCategory,
  dataDirectCosts,
  dataSalariesAssignedEmployees,
  dataProjectExpenses,
  dataGrossIncome,
  dataIndirectCosts,
  dataAdministrativePersonnel,
  dataEmployeesOnBench,
  dataExpenses,
  dataExpensesByCategory,
  dataNetIncome,
  currency,
  renderBy,
  selectedPeriod
) => {
  let treeArr = [];

  if (fullPeriod === null && dateColumnsPeriod.length === 12) fullPeriod = [...dateColumnsPeriod];
  if (selectedPeriod !== period && period) fullPeriod = [...dateColumnsPeriod];
  period = selectedPeriod;

  const createTree = () => {
    // Level 1
    // revenue = REVENUE (name in table)
    if (dataRevenue) {
      const revenue = {
        id: REVENUE + currency,
        name: REVENUE,
        value: getValue(fullPeriod || dateColumnsPeriod, dataRevenue, currency),
      };

      // Level 2
      // create and add children for revenue
      if (dataRevenueByCategory?.length > 0) {
        revenue.children = getChildren(fullPeriod || dateColumnsPeriod, dataRevenueByCategory, 'title', currency);
      }

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, revenue);
        revenue.value = getValue(tempPeriod, dataRevenue, currency);
        revenue.children = getChildren(tempPeriod, dataRevenueByCategory, 'title', currency);
      }

      treeArr.push(revenue);
    }

    // Level 1
    // otherIncome = Other Income (name in table)
    if (dataOtherIncome) {
      const otherIncome = {
        id: OTHER_INCOME + currency,
        name: OTHER_INCOME,
        value: getValue(fullPeriod || dateColumnsPeriod, dataOtherIncome, currency),
      };

      // Level 2
      // create and add children for otherIncome
      if (dataOtherIncomeByCategory?.length > 0) {
        otherIncome.children = getChildren(
          fullPeriod || dateColumnsPeriod,
          dataOtherIncomeByCategory,
          'title',
          currency
        );
      }

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, otherIncome);
        otherIncome.value = getValue(tempPeriod, dataOtherIncome, currency);
        otherIncome.children = getChildren(tempPeriod, dataOtherIncomeByCategory, 'title', currency);
      }

      treeArr.push(otherIncome);
    }

    // Level 2
    // salariesOfAssignedEmployees = Salaries of assigned employees (name in table)
    const salariesOfAssignedEmployees = {
      id: SALARIES_OF_ASSIGNED_EMPLOYEES + currency,
      name: SALARIES_OF_ASSIGNED_EMPLOYEES,
      value: getValue(fullPeriod || dateColumnsPeriod, dataSalariesAssignedEmployees, currency),
    };

    // Take values from tempPeriod until the dateColumnsPeriod array is ready
    // This is for progressively rendering of columns
    if (fullPeriod) {
      tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, salariesOfAssignedEmployees);
      salariesOfAssignedEmployees.value = getValue(tempPeriod, dataSalariesAssignedEmployees, currency);
    }

    // Level 2
    // projectExpenses = Project expenses (name in table)
    const projectExpenses = {
      id: PROJECT_EXPENSES + currency,
      name: PROJECT_EXPENSES,
      value: getValue(fullPeriod || dateColumnsPeriod, dataProjectExpenses, currency),
    };

    // Take values from tempPeriod until the dateColumnsPeriod array is ready
    // This is for progressively rendering of columns
    if (fullPeriod) {
      tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, projectExpenses);
      projectExpenses.value = getValue(tempPeriod, dataProjectExpenses, currency);
    }

    // Level 1
    // directCosts = Direct costs (name in table)
    if (dataDirectCosts) {
      const directCosts = {
        id: DIRECT_COSTS + currency,
        name: DIRECT_COSTS,
        value: getValue(fullPeriod || dateColumnsPeriod, dataDirectCosts, currency),
        children: [salariesOfAssignedEmployees, projectExpenses],
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, directCosts);
        directCosts.value = getValue(tempPeriod, dataDirectCosts, currency);
      }

      treeArr.push(directCosts);
    }

    // Level 1
    // grossIncome = Gross Income (name in table)
    if (dataGrossIncome) {
      const grossIncome = {
        id: GROSS_INCOME + currency,
        name: GROSS_INCOME,
        value: getValue(fullPeriod || dateColumnsPeriod, dataGrossIncome, currency),
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, grossIncome);
        grossIncome.value = getValue(tempPeriod, dataGrossIncome, currency);
      }

      treeArr.push(grossIncome);
    }

    // Level 2
    // administrativePersonnel = Administrative personnel (name in table)
    const administrativePersonnel = {
      id: ADMINISTRATIVE_PERSONNEL + currency,
      name: ADMINISTRATIVE_PERSONNEL,
      value: getValue(fullPeriod || dateColumnsPeriod, dataAdministrativePersonnel, currency),
    };

    // Take values from tempPeriod until the dateColumnsPeriod array is ready
    // This is for progressively rendering of columns
    if (fullPeriod) {
      tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, administrativePersonnel);
      administrativePersonnel.value = getValue(tempPeriod, dataAdministrativePersonnel, currency);
    }

    // Level 2
    // employeesOnBench = Employees on bench (name in table)
    const employeesOnBench = {
      id: EMPLOYEES_ON_BENCH + currency,
      name: EMPLOYEES_ON_BENCH,
      value: getValue(fullPeriod || dateColumnsPeriod, dataEmployeesOnBench, currency),
    };

    // Take values from tempPeriod until the dateColumnsPeriod array is ready
    // This is for progressively rendering of columns
    if (fullPeriod) {
      tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, employeesOnBench);
      employeesOnBench.value = getValue(tempPeriod, dataEmployeesOnBench, currency);
    }

    // Level 2
    // expenses = Expenses (name in table)
    const expenses = {
      id: EXPENSES + currency,
      name: EXPENSES,
      value: getValue(fullPeriod || dateColumnsPeriod, dataExpenses, currency),
    };

    // Level 3
    // create and add children for expenses
    if (dataExpenses && dataExpensesByCategory?.length > 0) {
      expenses.children = getChildren(fullPeriod || dateColumnsPeriod, dataExpensesByCategory, 'title', currency);
    }

    // Take values from tempPeriod until the dateColumnsPeriod array is ready
    // This is for progressively rendering of columns
    if (fullPeriod) {
      tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, expenses);
      expenses.value = getValue(tempPeriod, dataExpenses, currency);
      expenses.children = getChildren(tempPeriod, dataExpensesByCategory, 'title', currency);
    }

    // Level 1
    // indirectCosts = Indirect costs (name in table)
    if (dataIndirectCosts) {
      const indirectCosts = {
        id: INDIRECT_COSTS + currency,
        name: INDIRECT_COSTS,
        value: getValue(fullPeriod || dateColumnsPeriod, dataIndirectCosts, currency),
        children: [administrativePersonnel, employeesOnBench, expenses],
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, indirectCosts);
        indirectCosts.value = getValue(tempPeriod, dataIndirectCosts, currency);
      }

      treeArr.push(indirectCosts);
    }

    // Level 1
    // netIncome = Net Income (name in table)
    if (dataNetIncome) {
      const netIncome = {
        id: NET_INCOME + currency,
        name: NET_INCOME,
        value: getValue(fullPeriod || dateColumnsPeriod, dataNetIncome, currency),
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, netIncome);
        netIncome.value = getValue(tempPeriod, dataNetIncome, currency);
      }

      treeArr.push(netIncome);
    }
  };

  if (renderBy !== ALL_CURRENCIES) {
    createTree();
  }

  if (
    dateColumnsPeriod &&
    dataRevenue &&
    dataRevenueByCategory &&
    dataOtherIncome &&
    dataOtherIncomeByCategory &&
    dataDirectCosts &&
    dataSalariesAssignedEmployees &&
    dataProjectExpenses &&
    dataGrossIncome &&
    dataIndirectCosts &&
    dataAdministrativePersonnel &&
    dataEmployeesOnBench &&
    dataExpenses &&
    dataExpensesByCategory &&
    dataNetIncome &&
    fullPeriod
  ) {
    treeArr = [];
    createTree();
    fullPeriod = null;
    if (renderBy !== ALL_CURRENCIES) {
      tempPeriod = [];
    }
  }

  return treeArr;
};

let fullPeriodByProject = null;
let tempPeriodByProject = [];
let periodByProject = null;

const createTreeArrProject = (
  dateColumnsPeriod,
  dataRevenueByProject,
  dataRevenueByPC,
  dataDirectCostsByProject,
  dataSalariesByProject,
  dataSalariesBPE,
  dataExpensesByProject,
  dataExpensesBPC,
  dataGrossIncomeByProject,
  selectedPeriod
) => {
  let filterProjectByName = [];
  let treeArrByProject = [];

  if (fullPeriodByProject === null && dateColumnsPeriod.length === 12) fullPeriodByProject = [...dateColumnsPeriod];
  if (selectedPeriod !== periodByProject && periodByProject) fullPeriodByProject = [...dateColumnsPeriod];
  periodByProject = selectedPeriod;

  const createTree = () => {
    // Build tree if query returned dataRevenueByProject
    if (dataRevenueByProject) {
      filterProjectByName = uniqBy(dataRevenueByProject, 'project');

      let tempArr = [];

      //Level 1
      //Projects
      tempArr = filterProjectByName.map((item) => {
        const project = {
          id: item.project,
          name: item.project,
          value: getValueByProject(item.project, fullPeriodByProject || dateColumnsPeriod, dataRevenueByProject),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(tempPeriodByProject, fullPeriodByProject, dateColumnsPeriod, project);
          project.value = getValueByProject(item.project, tempPeriodByProject, dataRevenueByProject);
        }

        return project;
      });

      tempArr = uniqBy([...treeArrByProject, ...tempArr], 'name');

      treeArrByProject = tempArr.map((item) => {
        let newObj = { ...item, children: item.children || [] };

        //Level 2
        // revenueByProject = Revenue (name in table)
        if (dataRevenueByProject) {
          const revenueByProject = {
            id: REVENUE + item.name,
            name: REVENUE,
            value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataRevenueByProject),
          };

          //Level 3
          // create and add children for revenueByProject
          if (dataRevenueByPC?.length > 0) {
            getChildrenByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataRevenueByPC, 'title').length >
              0 &&
              (revenueByProject.children = getChildrenByProject(
                item.name,
                fullPeriodByProject || dateColumnsPeriod,
                dataRevenueByPC,
                'title'
              ));
          }

          // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
          // This is for progressively rendering of columns
          if (fullPeriodByProject) {
            tempPeriodByProject = getTempPeriod(
              tempPeriodByProject,
              fullPeriodByProject,
              dateColumnsPeriod,
              revenueByProject
            );
            revenueByProject.value = getValueByProject(item.project, tempPeriodByProject, dataRevenueByProject);
            revenueByProject.children = getChildrenByProject(item.name, tempPeriodByProject, dataRevenueByPC, 'title');
          }

          const revenueAlreadyExist = newObj.children.some((item) => item.name === REVENUE);
          if (revenueAlreadyExist) {
            newObj.children = newObj.children.map((item) => {
              if (item.name === REVENUE) return revenueByProject;
              else return item;
            });
          } else {
            newObj.children.push(revenueByProject);
          }
        }

        return newObj;
      });
    }

    //Rebuild tree if query returned dataDirectCostsByProject
    if (dataDirectCostsByProject) {
      filterProjectByName = uniqBy(dataDirectCostsByProject, 'project');

      let tempArr = [];

      //Level 1
      //Projects
      tempArr = filterProjectByName.map((item) => {
        const project = {
          id: item.project,
          name: item.project,
          value: getValueByProject(item.project, fullPeriodByProject || dateColumnsPeriod, dataDirectCostsByProject),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(tempPeriodByProject, fullPeriodByProject, dateColumnsPeriod, project);
          project.value = getValueByProject(item.project, tempPeriodByProject, dataDirectCostsByProject);
        }

        return project;
      });

      tempArr = uniqBy([...treeArrByProject, ...tempArr], 'name');

      treeArrByProject = tempArr.map((item) => {
        let newObj = { ...item, children: item.children || [] };

        //Level 2
        // directCostsByProject = Direct costs (name in table)
        if (dataDirectCostsByProject) {
          const directCostsByProject = {
            id: DIRECT_COSTS + item.name,
            name: DIRECT_COSTS,
            value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataDirectCostsByProject),
            children: [],
          };

          // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
          // This is for progressively rendering of columns
          if (fullPeriodByProject) {
            tempPeriodByProject = getTempPeriod(
              tempPeriodByProject,
              fullPeriodByProject,
              dateColumnsPeriod,
              directCostsByProject
            );
            directCostsByProject.value = getValueByProject(item.project, tempPeriodByProject, dataDirectCostsByProject);
          }

          //Level 3
          if (dataSalariesByProject) {
            // salaries = Salaries (name in table)
            const salaries = {
              id: SALARIES + item.name,
              name: SALARIES,
              value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataSalariesByProject),
            };

            // Level 4
            // create and add children for salaries
            if (dataSalariesBPE?.length > 0) {
              const childrenSalariesBPE = getChildrenByProject(
                item.name,
                fullPeriodByProject || dateColumnsPeriod,
                dataSalariesBPE,
                'title'
              );
              if (childrenSalariesBPE.length > 0) salaries.children = childrenSalariesBPE;
            }

            // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
            // This is for progressively rendering of columns
            if (fullPeriodByProject) {
              tempPeriodByProject = getTempPeriod(
                tempPeriodByProject,
                fullPeriodByProject,
                dateColumnsPeriod,
                salaries
              );
              salaries.value = getValueByProject(item.name, tempPeriodByProject, dataSalariesByProject);
              salaries.children = getChildrenByProject(item.name, tempPeriodByProject, dataSalariesBPE, 'title');
            }

            directCostsByProject.children.push(salaries);
          }

          //Level 3
          if (dataExpensesByProject) {
            // expenses = Expenses (name in table)
            const expenses = {
              id: EXPENSES + item.name,
              name: EXPENSES,
              value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataExpensesByProject),
            };

            // Level 4
            // create and add children for expenses
            if (dataExpensesBPC?.length > 0) {
              const childrenExpensesBPC = getChildrenByProject(
                item.name,
                fullPeriodByProject || dateColumnsPeriod,
                dataExpensesBPC,
                'title'
              );
              if (childrenExpensesBPC.length > 0) expenses.children = childrenExpensesBPC;
            }

            // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
            // This is for progressively rendering of columns
            if (fullPeriodByProject) {
              tempPeriodByProject = getTempPeriod(
                tempPeriodByProject,
                fullPeriodByProject,
                dateColumnsPeriod,
                expenses
              );
              expenses.value = getValueByProject(item.project, tempPeriodByProject, dataExpensesByProject);
              expenses.children = getChildrenByProject(item.name, tempPeriodByProject, dataExpensesBPC, 'title');
            }

            directCostsByProject.children.push(expenses);
          }

          const directCostsAlreadyExist = newObj.children.some((item) => item.name === DIRECT_COSTS);
          if (directCostsAlreadyExist) {
            newObj.children = newObj.children.map((item) => {
              if (item.name === DIRECT_COSTS) return directCostsByProject;
              else return item;
            });
          } else {
            newObj.children.push(directCostsByProject);
          }
        }

        return newObj;
      });
    }

    // Rebuild tree if query returned dataGrossIncomeByProject
    if (dataGrossIncomeByProject) {
      let tempArr = [];
      filterProjectByName = uniqBy(dataGrossIncomeByProject, 'project');

      //Level 1
      //Projects
      tempArr = filterProjectByName.map((item) => {
        const project = {
          id: item.project,
          name: item.project,
          value: getValueByProject(item.project, fullPeriodByProject || dateColumnsPeriod, dataGrossIncomeByProject),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(tempPeriodByProject, fullPeriodByProject, dateColumnsPeriod, project);
          project.value = getValueByProject(item.project, tempPeriodByProject, dataGrossIncomeByProject);
        }

        return project;
      });

      // Move children from treeArrByProject to tempArr
      tempArr = tempArr.map((item) => {
        treeArrByProject.forEach((item2) => {
          if (item.name === item2.name) item.children = [...item2.children];
        });
        return item;
      });

      //Projects
      treeArrByProject = tempArr.map((item, i) => {
        let newObj = { ...item, children: item.children || [] };
        //Level 2
        //grossIncomeByProject = Gross Income by the project(name in table)
        const grossIncomeByProject = {
          id: GROSS_INCOME + item.name,
          name: GROSS_INCOME,
          value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataGrossIncomeByProject),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(
            tempPeriodByProject,
            fullPeriodByProject,
            dateColumnsPeriod,
            grossIncomeByProject
          );
          grossIncomeByProject.value = getValueByProject(item.project, tempPeriodByProject, dataGrossIncomeByProject);
        }

        const grossIncomeByProjectAlreadyExist = newObj.children.some((item) => item.name === GROSS_INCOME);
        if (grossIncomeByProjectAlreadyExist) {
          newObj.children = newObj.children.map((item) => {
            if (item.name === GROSS_INCOME) return grossIncomeByProject;
            else return item;
          });
        } else {
          newObj.children.push(grossIncomeByProject);
        }
        return newObj;
      });
    }

    // Sorting children in a correct order
    treeArrByProject = treeArrByProject.map((item) => {
      const newChildrenOrder = item.children.reduce((sum, current) => {
        if (current?.name === 'Revenue' && current.value) sum[0] = current;
        if (current?.name === 'Direct costs' && current.value) sum[1] = current;
        if (current?.name === 'Gross Income' && current.value) sum[2] = current;
        return sum;
      }, []);
      item.children = [...newChildrenOrder].filter((item) => item !== undefined);
      return item;
    });
  };

  createTree();

  if (
    dateColumnsPeriod &&
    dataRevenueByProject &&
    dataRevenueByPC &&
    dataDirectCostsByProject &&
    dataSalariesByProject &&
    dataSalariesBPE &&
    dataExpensesByProject &&
    dataExpensesBPC &&
    dataGrossIncomeByProject &&
    fullPeriodByProject
  ) {
    treeArrByProject = [];
    createTree();
    fullPeriodByProject = null;
    tempPeriodByProject = [];
  }
  return sortBy(treeArrByProject, 'name');
};

let fullPeriodAllCurrency = null;

const createTreeArrCurrency = (
  dateColumnsPeriod,
  dataRevenue,
  dataRevenueByCategory,
  dataOtherIncome,
  dataOtherIncomeByCategory,
  dataDirectCosts,
  dataSalariesAssignedEmployees,
  dataProjectExpenses,
  dataGrossIncome,
  dataIndirectCosts,
  dataAdministrativePersonnel,
  dataEmployeesOnBench,
  dataExpenses,
  dataExpensesByCategory,
  dataNetIncome,
  selectedPeriod
) => {
  let allCurrencies = [];
  let treeArrAllCurrency = [];

  if (fullPeriodAllCurrency === null && dateColumnsPeriod.length === 12) fullPeriodAllCurrency = [...dateColumnsPeriod];

  const createTree = () => {
    const getMergedCurrencies = (prevCurrencies, data) => {
      return [...new Set([...prevCurrencies, ...data.map((item) => item.currency)])];
    };

    if (dataRevenue) allCurrencies = getMergedCurrencies(allCurrencies, dataRevenue);
    if (dataRevenueByCategory) allCurrencies = getMergedCurrencies(allCurrencies, dataRevenueByCategory);
    if (dataOtherIncome) allCurrencies = getMergedCurrencies(allCurrencies, dataOtherIncome);
    if (dataOtherIncomeByCategory) allCurrencies = getMergedCurrencies(allCurrencies, dataOtherIncomeByCategory);
    if (dataDirectCosts) allCurrencies = getMergedCurrencies(allCurrencies, dataDirectCosts);
    if (dataSalariesAssignedEmployees)
      allCurrencies = getMergedCurrencies(allCurrencies, dataSalariesAssignedEmployees);
    if (dataExpenses) allCurrencies = getMergedCurrencies(allCurrencies, dataExpenses);
    if (dataExpensesByCategory) allCurrencies = getMergedCurrencies(allCurrencies, dataExpensesByCategory);
    if (dataProjectExpenses) allCurrencies = getMergedCurrencies(allCurrencies, dataProjectExpenses);
    if (dataGrossIncome) allCurrencies = getMergedCurrencies(allCurrencies, dataGrossIncome);
    if (dataIndirectCosts) allCurrencies = getMergedCurrencies(allCurrencies, dataIndirectCosts);
    if (dataAdministrativePersonnel) allCurrencies = getMergedCurrencies(allCurrencies, dataAdministrativePersonnel);
    if (dataEmployeesOnBench) allCurrencies = getMergedCurrencies(allCurrencies, dataEmployeesOnBench);
    if (dataNetIncome) allCurrencies = getMergedCurrencies(allCurrencies, dataNetIncome);

    if (allCurrencies.length > 0) {
      treeArrAllCurrency = allCurrencies.map((itemCurrency) => {
        const currency = {
          id: itemCurrency,
          name: itemCurrency,
          // value: Add value after created children from 'Net cash flow'
        };

        currency.children = createTreeArr(
          dateColumnsPeriod,
          dataRevenue || [],
          dataRevenueByCategory || [],
          dataOtherIncome || [],
          dataOtherIncomeByCategory || [],
          dataDirectCosts || [],
          dataSalariesAssignedEmployees || [],
          dataProjectExpenses || [],
          dataGrossIncome || [],
          dataIndirectCosts || [],
          dataAdministrativePersonnel || [],
          dataEmployeesOnBench || [],
          dataExpenses || [],
          dataExpensesByCategory || [],
          dataNetIncome || [],
          itemCurrency,
          ALL_CURRENCIES,
          selectedPeriod
        );

        // Add value from 'Net cash flow'
        currency.children.forEach((item) => {
          if (item.name === 'Net Income') {
            currency.value = item.value;
          }
        });
        return currency;
      });
    }
  };

  createTree();

  if (
    dataRevenue &&
    dataRevenueByCategory &&
    dataOtherIncome &&
    dataOtherIncomeByCategory &&
    dataDirectCosts &&
    dataSalariesAssignedEmployees &&
    dataProjectExpenses &&
    dataGrossIncome &&
    dataIndirectCosts &&
    dataAdministrativePersonnel &&
    dataEmployeesOnBench &&
    dataExpenses &&
    dataExpensesByCategory &&
    dataNetIncome
  ) {
    createTree();
  }
  return treeArrAllCurrency;
};

export { createTreeArr, createTreeArrProject, createTreeArrCurrency };
